import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FaCheck, FaLink, FaPlus, FaSearch, FaToggleOff, FaToggleOn, FaTrash, FaTrashAlt } from 'react-icons/fa';
import DataTable from '../DataTable/DataTable.jsx';
import Pagination from '../Pagination/Pagination.jsx';
import ajudaApi from '../../services/ajudaApi.js';
import { colors } from '../../utils/colors.js';
import { PUBLIC_APP_URL } from '../../config/api.js';

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('pt-BR') : '—';
}

const MOTIVO_LABELS = {
  outro: 'Outro',
  nao_funciona: 'Não funciona',
  confuso: 'Confuso',
  politica: 'Política',
};

const EMPTY_ITEM_FORM = {
  titulo: '',
  categoria: '',
  texto: '',
  video_url: '',
  ativo: true,
  ordem: 0,
  tags: '',
};

function itemToForm(item) {
  return {
    titulo: item.titulo,
    categoria: item.categoria,
    texto: item.texto || '',
    video_url: item.video_url || '',
    ativo: item.ativo,
    ordem: item.ordem ?? 0,
    tags: (item.tags || []).join(', '),
  };
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${colors.gray[900]};
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${colors.gray[500]};
  margin-top: 4px;
`;

const TabsBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${colors.gray[200]};
  margin-bottom: 20px;
`;

const TabButton = styled.button`
  padding: 10px 4px;
  margin-right: 24px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.$active ? colors.primary[600] : colors.gray[500])};
  border-bottom: 2px solid ${(props) => (props.$active ? colors.primary[600] : 'transparent')};
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const SearchBox = styled.div`
  position: relative;
  min-width: 240px;
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.gray[400]};
  font-size: 13px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 34px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
`;

const FilterInput = styled.input`
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: ${colors.gray[700]};
`;

const FilterSelect = styled.select`
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: ${colors.gray[700]};
`;

const DateFieldGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DateFieldLabel = styled.span`
  font-size: 12px;
  color: ${colors.gray[500]};
`;

const DateInput = styled.input`
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 14px;
  color: ${colors.gray[700]};
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${colors.gradients.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
`;

const SubText = styled.div`
  font-size: 12px;
  color: ${colors.gray[500]};
`;

const Badge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${(props) => {
    if (props.$tone === 'success') return colors.success.light;
    if (props.$tone === 'error') return colors.error.light;
    return colors.gray[100];
  }};
  color: ${(props) => {
    if (props.$tone === 'success') return colors.success.dark;
    if (props.$tone === 'error') return colors.error.dark;
    return colors.gray[600];
  }};
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${(props) => (props.$danger ? colors.error.main : colors.gray[500])};
  font-size: 14px;
  padding: 6px;

  &:hover {
    color: ${(props) => (props.$danger ? colors.error.dark : colors.primary[600])};
  }
`;

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  color: ${colors.gray[500]};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: stretch;
  }
`;

const ModalCard = styled.form`
  background: ${colors.white};
  overflow-y: auto;
  width: 480px;
  max-width: 100%;
  height: 100vh;
  padding: 28px;

  @media (max-width: 768px) {
    width: 100vw;
  }
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  margin: 0 0 20px;
  color: ${colors.gray[900]};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.$cols || '1fr'};
  gap: 12px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.gray[700]};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  min-height: 120px;
  resize: vertical;
`;

const OrdemInput = styled.input`
  width: 60px;
  padding: 6px 8px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${colors.gray[700]};
  margin-bottom: 16px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: ${colors.gray[100]};
  color: ${colors.gray[700]};
`;

const SaveButton = styled(Button)`
  background: ${colors.gradients.primary};
  color: ${colors.white};
`;

const ErrorBox = styled.div`
  background: ${colors.error.light};
  color: ${colors.error.dark};
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const SuccessBox = styled.div`
  background: ${colors.success.light};
  color: ${colors.success.dark};
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const DEBOUNCE_MS = 400;

export default function AjudaManager() {
  const [tab, setTab] = useState('itens');

  // --- Resumo ---
  const [resumo, setResumo] = useState(null);

  const loadResumo = useCallback(() => {
    ajudaApi
      .resumo()
      .then(setResumo)
      .catch(() => setResumo(null));
  }, []);

  useEffect(() => {
    loadResumo();
  }, [loadResumo]);

  // --- Itens tab ---
  const [items, setItems] = useState(null);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsSearch, setItemsSearch] = useState('');
  const [itemsDebouncedSearch, setItemsDebouncedSearch] = useState('');
  const [itemsCategoria, setItemsCategoria] = useState('');
  const [itemsAtivo, setItemsAtivo] = useState('');
  const [itemsTag, setItemsTag] = useState('');
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [copiedId, setCopiedId] = useState(null);
  const itemsDebounceRef = useRef(null);

  useEffect(() => {
    if (itemsDebounceRef.current) clearTimeout(itemsDebounceRef.current);
    itemsDebounceRef.current = setTimeout(() => {
      setItemsDebouncedSearch(itemsSearch);
      setItemsPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(itemsDebounceRef.current);
  }, [itemsSearch]);

  const loadItems = useCallback(() => {
    setItemsLoading(true);
    const params = { page: itemsPage, per_page: itemsPerPage };
    if (itemsDebouncedSearch) params.search = itemsDebouncedSearch;
    if (itemsCategoria) params.categoria = itemsCategoria;
    if (itemsAtivo) params.ativo = itemsAtivo === 'true';
    if (itemsTag) params.tag = itemsTag;
    ajudaApi
      .list(params)
      .then((data) => {
        setItems(data.items || []);
        setItemsTotal(data.total || 0);
      })
      .catch(() => {
        setItems([]);
        setItemsTotal(0);
      })
      .finally(() => setItemsLoading(false));
  }, [itemsPage, itemsPerPage, itemsDebouncedSearch, itemsCategoria, itemsAtivo, itemsTag]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM_FORM);
  const [itemFormError, setItemFormError] = useState('');
  const [itemSaving, setItemSaving] = useState(false);
  const [itemsMessage, setItemsMessage] = useState('');

  const openCreateItem = () => {
    setEditingItem(null);
    setItemForm(EMPTY_ITEM_FORM);
    setItemFormError('');
    setShowItemModal(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm(itemToForm(item));
    setItemFormError('');
    setShowItemModal(true);
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    setItemFormError('');
    setItemSaving(true);
    const payload = {
      titulo: itemForm.titulo,
      categoria: itemForm.categoria,
      texto: itemForm.texto,
      video_url: itemForm.video_url || null,
      ativo: itemForm.ativo,
      ordem: Number(itemForm.ordem) || 0,
      tags: itemForm.tags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
    };
    try {
      if (editingItem) {
        await ajudaApi.update(editingItem.id, payload);
      } else {
        await ajudaApi.create(payload);
      }
      setShowItemModal(false);
      loadItems();
      loadResumo();

      const foraDoFiltro =
        (itemsCategoria && payload.categoria !== itemsCategoria) ||
        (itemsAtivo && String(payload.ativo) !== itemsAtivo) ||
        (itemsTag && !payload.tags.includes(itemsTag.trim().toLowerCase()));
      setItemsMessage(
        foraDoFiltro
          ? 'Salvo com sucesso — o item não aparece na lista abaixo porque não corresponde aos filtros ativos (categoria/tag/status).'
          : 'Salvo com sucesso.'
      );
      setTimeout(() => setItemsMessage(''), foraDoFiltro ? 6000 : 3000);
    } catch (err) {
      setItemFormError(err.response?.data?.detail || 'Não foi possível salvar o conteúdo de ajuda.');
    } finally {
      setItemSaving(false);
    }
  };

  const handleToggleItemAtivo = async (item) => {
    try {
      await ajudaApi.update(item.id, { ativo: !item.ativo });
      loadItems();
      loadResumo();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível alterar o status do item.');
    }
  };

  const handleDeletePermanenteItem = async (item) => {
    if (
      !window.confirm(
        `Excluir DEFINITIVAMENTE o conteúdo "${item.titulo}"? Essa ação não pode ser desfeita e também apaga os feedbacks associados a ele.`
      )
    )
      return;
    try {
      await ajudaApi.deletePermanente(item.id);
      loadItems();
      loadResumo();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível excluir definitivamente o conteúdo de ajuda.');
    }
  };

  const handleUpdateOrdem = async (item, novaOrdem) => {
    if (novaOrdem === item.ordem) return;
    try {
      await ajudaApi.update(item.id, { ordem: novaOrdem });
      loadItems();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível alterar a ordem do item.');
    }
  };

  const handleCopyLink = async (item) => {
    const url = `${PUBLIC_APP_URL}/ajuda/${item.id}`;
    try {
      await copyToClipboard(url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId((current) => (current === item.id ? null : current)), 2000);
    } catch {
      window.alert('Não foi possível copiar o link.');
    }
  };

  const itemColumns = [
    {
      key: 'ordem',
      label: 'Ordem',
      render: (row) => (
        <OrdemInput
          type="number"
          defaultValue={row.ordem ?? 0}
          key={`${row.id}-${row.ordem}`}
          onBlur={(e) => handleUpdateOrdem(row, Number(e.target.value) || 0)}
        />
      ),
    },
    { key: 'titulo', label: 'Título', render: (row) => row.titulo },
    { key: 'categoria', label: 'Categoria', render: (row) => row.categoria },
    {
      key: 'tags',
      label: 'Tags',
      render: (row) =>
        row.tags?.length ? (
          <>
            {row.tags.map((t) => (
              <Badge key={t} $tone="gray" style={{ marginRight: 4 }}>
                {t}
              </Badge>
            ))}
          </>
        ) : (
          '—'
        ),
    },
    {
      key: 'ativo',
      label: 'Status',
      render: (row) => <Badge $tone={row.ativo ? 'success' : 'gray'}>{row.ativo ? 'Ativo' : 'Inativo'}</Badge>,
    },
    { key: 'video_url', label: 'Vídeo', render: (row) => (row.video_url ? 'Sim' : '—') },
    { key: 'created_at', label: 'Criado em', render: (row) => formatDateTime(row.created_at) },
    {
      key: 'acoes',
      label: 'Ações',
      render: (row) => (
        <>
          <IconButton type="button" onClick={() => handleCopyLink(row)} title="Copiar link direto">
            {copiedId === row.id ? <FaCheck /> : <FaLink />}
          </IconButton>
          <IconButton type="button" onClick={() => openEditItem(row)} title="Editar">
            Editar
          </IconButton>
          <IconButton
            type="button"
            onClick={() => handleToggleItemAtivo(row)}
            title={row.ativo ? 'Desativar (reversível)' : 'Ativar'}
          >
            {row.ativo ? <FaToggleOn /> : <FaToggleOff />}
          </IconButton>
          <IconButton
            type="button"
            $danger
            onClick={() => handleDeletePermanenteItem(row)}
            title="Excluir definitivamente (não pode ser desfeito)"
          >
            <FaTrashAlt />
          </IconButton>
        </>
      ),
    },
  ];

  // --- Feedback tab ---
  const [feedbackItems, setFeedbackItems] = useState(null);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [feedbackUtil, setFeedbackUtil] = useState('');
  const [feedbackMotivo, setFeedbackMotivo] = useState('');
  const [feedbackDataInicio, setFeedbackDataInicio] = useState('');
  const [feedbackDataFim, setFeedbackDataFim] = useState('');
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [feedbackPerPage, setFeedbackPerPage] = useState(20);

  const loadFeedback = useCallback(() => {
    setFeedbackLoading(true);
    const params = { page: feedbackPage, per_page: feedbackPerPage };
    if (feedbackUtil) params.util = feedbackUtil === 'true';
    if (feedbackMotivo) params.motivo = feedbackMotivo;
    if (feedbackDataInicio) params.data_inicio = feedbackDataInicio;
    if (feedbackDataFim) params.data_fim = feedbackDataFim;
    ajudaApi
      .listFeedback(params)
      .then((data) => {
        setFeedbackItems(data.items || []);
        setFeedbackTotal(data.total || 0);
      })
      .catch(() => {
        setFeedbackItems([]);
        setFeedbackTotal(0);
      })
      .finally(() => setFeedbackLoading(false));
  }, [feedbackPage, feedbackPerPage, feedbackUtil, feedbackMotivo, feedbackDataInicio, feedbackDataFim]);

  useEffect(() => {
    if (tab === 'feedback') loadFeedback();
  }, [tab, loadFeedback]);

  const handleDeleteFeedback = async (row) => {
    if (!window.confirm('Excluir este feedback?')) return;
    try {
      await ajudaApi.deleteFeedback(row.id);
      loadFeedback();
      loadResumo();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível excluir o feedback.');
    }
  };

  const feedbackColumns = [
    { key: 'ajuda_titulo', label: 'Item', render: (row) => row.ajuda_titulo || '—' },
    {
      key: 'usuario',
      label: 'Usuário',
      render: (row) => (
        <div>
          <div>{row.usuario_nome || 'Anônimo'}</div>
          <SubText>{row.usuario_email || row.device_id || '—'}</SubText>
        </div>
      ),
    },
    {
      key: 'util',
      label: 'Útil?',
      render: (row) => <Badge $tone={row.util ? 'success' : 'error'}>{row.util ? 'Sim' : 'Não'}</Badge>,
    },
    { key: 'motivo', label: 'Motivo', render: (row) => (row.motivo ? MOTIVO_LABELS[row.motivo] || row.motivo : '—') },
    { key: 'comentario', label: 'Comentário', render: (row) => row.comentario || '—' },
    { key: 'created_at', label: 'Data', render: (row) => formatDateTime(row.created_at) },
    {
      key: 'acoes',
      label: 'Ações',
      render: (row) => (
        <IconButton type="button" $danger onClick={() => handleDeleteFeedback(row)} title="Excluir">
          <FaTrash />
        </IconButton>
      ),
    },
  ];

  // --- Insights tab ---
  const [insightItems, setInsightItems] = useState(null);
  const [insightTotal, setInsightTotal] = useState(0);
  const [insightLoading, setInsightLoading] = useState(true);
  const [insightCategoria, setInsightCategoria] = useState('');
  const [insightDataInicio, setInsightDataInicio] = useState('');
  const [insightDataFim, setInsightDataFim] = useState('');
  const [insightPage, setInsightPage] = useState(1);
  const [insightPerPage, setInsightPerPage] = useState(20);

  const loadInsights = useCallback(() => {
    setInsightLoading(true);
    const params = { page: insightPage, per_page: insightPerPage };
    if (insightCategoria) params.categoria = insightCategoria;
    if (insightDataInicio) params.data_inicio = insightDataInicio;
    if (insightDataFim) params.data_fim = insightDataFim;
    ajudaApi
      .listInsights(params)
      .then((data) => {
        setInsightItems(data.items || []);
        setInsightTotal(data.total || 0);
      })
      .catch(() => {
        setInsightItems([]);
        setInsightTotal(0);
      })
      .finally(() => setInsightLoading(false));
  }, [insightPage, insightPerPage, insightCategoria, insightDataInicio, insightDataFim]);

  useEffect(() => {
    if (tab === 'insights') loadInsights();
  }, [tab, loadInsights]);

  const handleDeleteInsight = async (row) => {
    if (!window.confirm('Excluir este insight?')) return;
    try {
      await ajudaApi.deleteInsights(row.id);
      loadInsights();
      loadResumo();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível excluir o insight.');
    }
  };

  const insightColumns = [
    { key: 'ajuda_titulo', label: 'Item', render: (row) => row.ajuda_titulo || '—' },
    { key: 'categoria', label: 'Categoria', render: (row) => row.categoria },
    { key: 'evento', label: 'Evento', render: (row) => row.evento },
    {
      key: 'usuario',
      label: 'Usuário',
      render: (row) => (
        <div>
          <div>{row.usuario_nome || 'Anônimo'}</div>
          <SubText>{row.usuario_email || row.device_id || '—'}</SubText>
        </div>
      ),
    },
    { key: 'created_at', label: 'Data', render: (row) => formatDateTime(row.created_at) },
    {
      key: 'acoes',
      label: 'Ações',
      render: (row) => (
        <IconButton type="button" $danger onClick={() => handleDeleteInsight(row)} title="Excluir">
          <FaTrash />
        </IconButton>
      ),
    },
  ];

  return (
    <div>
      <Header>
        <Title>Ajuda</Title>
      </Header>

      <StatsRow>
        <StatCard>
          <StatValue>{resumo ? resumo.itens_ativos : '—'}</StatValue>
          <StatLabel>Itens ativos ({resumo ? resumo.itens_total : '—'} no total)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{resumo ? resumo.feedback_util : '—'}</StatValue>
          <StatLabel>Feedback útil ({resumo ? resumo.feedback_nao_util : '—'} não útil)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{resumo ? resumo.insights_total : '—'}</StatValue>
          <StatLabel>Insights registrados</StatLabel>
        </StatCard>
      </StatsRow>

      <TabsBar>
        <TabButton type="button" $active={tab === 'itens'} onClick={() => setTab('itens')}>
          Itens
        </TabButton>
        <TabButton type="button" $active={tab === 'feedback'} onClick={() => setTab('feedback')}>
          Feedback
        </TabButton>
        <TabButton type="button" $active={tab === 'insights'} onClick={() => setTab('insights')}>
          Insights
        </TabButton>
      </TabsBar>

      {tab === 'itens' && (
        <>
          {itemsMessage && <SuccessBox>{itemsMessage}</SuccessBox>}
          <Header>
            <FilterBar style={{ marginBottom: 0 }}>
              <SearchBox>
                <SearchIcon />
                <SearchInput
                  placeholder="Buscar por título..."
                  value={itemsSearch}
                  onChange={(e) => setItemsSearch(e.target.value)}
                />
              </SearchBox>
              <FilterInput
                placeholder="Categoria"
                value={itemsCategoria}
                onChange={(e) => {
                  setItemsCategoria(e.target.value);
                  setItemsPage(1);
                }}
              />
              <FilterInput
                placeholder="Tag"
                value={itemsTag}
                onChange={(e) => {
                  setItemsTag(e.target.value);
                  setItemsPage(1);
                }}
              />
              <FilterSelect
                value={itemsAtivo}
                onChange={(e) => {
                  setItemsAtivo(e.target.value);
                  setItemsPage(1);
                }}
              >
                <option value="">Todos os status</option>
                <option value="true">Ativos</option>
                <option value="false">Inativos</option>
              </FilterSelect>
            </FilterBar>
            <AddButton type="button" onClick={openCreateItem}>
              <FaPlus /> Novo item
            </AddButton>
          </Header>

          <DataTable
            columns={itemColumns}
            rows={items}
            loading={itemsLoading}
            emptyMessage="Nenhum conteúdo de ajuda encontrado."
          />
          {!itemsLoading && (
            <Pagination
              page={itemsPage}
              perPage={itemsPerPage}
              total={itemsTotal}
              onPageChange={setItemsPage}
              onPerPageChange={(value) => {
                setItemsPerPage(value);
                setItemsPage(1);
              }}
            />
          )}
        </>
      )}

      {tab === 'feedback' && (
        <>
          <FilterBar>
            <FilterSelect
              value={feedbackUtil}
              onChange={(e) => {
                setFeedbackUtil(e.target.value);
                setFeedbackPage(1);
              }}
            >
              <option value="">Útil e não útil</option>
              <option value="true">Só útil</option>
              <option value="false">Só não útil</option>
            </FilterSelect>
            <FilterSelect
              value={feedbackMotivo}
              onChange={(e) => {
                setFeedbackMotivo(e.target.value);
                setFeedbackPage(1);
              }}
            >
              <option value="">Todos os motivos</option>
              {Object.keys(MOTIVO_LABELS).map((m) => (
                <option key={m} value={m}>
                  {MOTIVO_LABELS[m]}
                </option>
              ))}
            </FilterSelect>
            <DateFieldGroup>
              <DateFieldLabel>De</DateFieldLabel>
              <DateInput
                type="date"
                value={feedbackDataInicio}
                onChange={(e) => {
                  setFeedbackDataInicio(e.target.value);
                  setFeedbackPage(1);
                }}
              />
            </DateFieldGroup>
            <DateFieldGroup>
              <DateFieldLabel>Até</DateFieldLabel>
              <DateInput
                type="date"
                value={feedbackDataFim}
                onChange={(e) => {
                  setFeedbackDataFim(e.target.value);
                  setFeedbackPage(1);
                }}
              />
            </DateFieldGroup>
          </FilterBar>

          <DataTable
            columns={feedbackColumns}
            rows={feedbackItems}
            loading={feedbackLoading}
            emptyMessage="Nenhum feedback encontrado."
          />
          {!feedbackLoading && (
            <Pagination
              page={feedbackPage}
              perPage={feedbackPerPage}
              total={feedbackTotal}
              onPageChange={setFeedbackPage}
              onPerPageChange={(value) => {
                setFeedbackPerPage(value);
                setFeedbackPage(1);
              }}
            />
          )}
        </>
      )}

      {tab === 'insights' && (
        <>
          <FilterBar>
            <FilterInput
              placeholder="Categoria"
              value={insightCategoria}
              onChange={(e) => {
                setInsightCategoria(e.target.value);
                setInsightPage(1);
              }}
            />
            <DateFieldGroup>
              <DateFieldLabel>De</DateFieldLabel>
              <DateInput
                type="date"
                value={insightDataInicio}
                onChange={(e) => {
                  setInsightDataInicio(e.target.value);
                  setInsightPage(1);
                }}
              />
            </DateFieldGroup>
            <DateFieldGroup>
              <DateFieldLabel>Até</DateFieldLabel>
              <DateInput
                type="date"
                value={insightDataFim}
                onChange={(e) => {
                  setInsightDataFim(e.target.value);
                  setInsightPage(1);
                }}
              />
            </DateFieldGroup>
          </FilterBar>

          <DataTable
            columns={insightColumns}
            rows={insightItems}
            loading={insightLoading}
            emptyMessage="Nenhum insight encontrado."
          />
          {!insightLoading && (
            <Pagination
              page={insightPage}
              perPage={insightPerPage}
              total={insightTotal}
              onPageChange={setInsightPage}
              onPerPageChange={(value) => {
                setInsightPerPage(value);
                setInsightPage(1);
              }}
            />
          )}
        </>
      )}

      {showItemModal &&
        createPortal(
          <ModalOverlay onClick={() => setShowItemModal(false)}>
            <ModalCard onClick={(e) => e.stopPropagation()} onSubmit={handleSubmitItem}>
              <ModalTitle>{editingItem ? 'Editar conteúdo de ajuda' : 'Novo conteúdo de ajuda'}</ModalTitle>

              {itemFormError && <ErrorBox>{itemFormError}</ErrorBox>}

              <Label>Título</Label>
              <Input
                required
                maxLength={200}
                value={itemForm.titulo}
                onChange={(e) => setItemForm({ ...itemForm, titulo: e.target.value })}
              />

              <Row $cols="1fr 100px">
                <div>
                  <Label>Categoria</Label>
                  <Input
                    required
                    maxLength={100}
                    value={itemForm.categoria}
                    onChange={(e) => setItemForm({ ...itemForm, categoria: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Ordem</Label>
                  <Input
                    type="number"
                    value={itemForm.ordem}
                    onChange={(e) => setItemForm({ ...itemForm, ordem: e.target.value })}
                  />
                </div>
              </Row>

              <Label>Texto</Label>
              <TextArea
                required
                value={itemForm.texto}
                onChange={(e) => setItemForm({ ...itemForm, texto: e.target.value })}
              />

              <Label>URL do vídeo (opcional)</Label>
              <Input
                value={itemForm.video_url}
                onChange={(e) => setItemForm({ ...itemForm, video_url: e.target.value })}
              />

              <Label>Tags (separadas por vírgula)</Label>
              <Input
                placeholder="ferramenta, pagamento"
                value={itemForm.tags}
                onChange={(e) => setItemForm({ ...itemForm, tags: e.target.value })}
              />

              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={itemForm.ativo}
                  onChange={(e) => setItemForm({ ...itemForm, ativo: e.target.checked })}
                />
                Ativo (visível para os usuários)
              </CheckboxLabel>

              <ModalActions>
                <CancelButton type="button" onClick={() => setShowItemModal(false)}>
                  Cancelar
                </CancelButton>
                <SaveButton type="submit" disabled={itemSaving}>
                  {itemSaving ? 'Salvando...' : 'Salvar'}
                </SaveButton>
              </ModalActions>
            </ModalCard>
          </ModalOverlay>,
          document.body
        )}
    </div>
  );
}
